import styled from '@emotion/styled';
import { ReactNode } from 'react';

import ExternalLinkIcon from '@/assets/svg/ui-elements/arrow-up-right.svg';
import AlertIcon from '@/assets/svg/ui-elements/status-alert-circle.svg';
import CheckIcon from '@/assets/svg/ui-elements/status-check-circle.svg';
import { getSuiExplorerTransactionUrl } from '@/config/sui';
import { Link, Loader, Typography } from '@/design-system';
import { theme } from '@/design-system/theme';

const Root = styled('div')(({ theme: t }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: t.spacing[16],
  textAlign: 'center',
}));

const Glyph = styled('div', {
  shouldForwardProp: (prop) => prop !== '$tone',
})<{ $tone: 'success' | 'error' }>(({ theme: t, $tone }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  flexShrink: 0,
  color: $tone === 'success' ? t.colors.brand.solid : t.colors.error.solid,

  '& svg': { width: '100%', height: '100%' },
}));

const LoaderSlot = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 48,
  height: 48,
  flexShrink: 0,
});

const TextGroup = styled('div')(({ theme: t }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: t.spacing[4],
}));

const StatusScreen = ({ glyph, children }: { glyph: ReactNode; children: ReactNode }) => (
  <Root role="status" aria-live="polite">
    {glyph}
    {children}
  </Root>
);

export const SendLoadingScreen = () => (
  <StatusScreen
    glyph={
      <LoaderSlot>
        <Loader />
      </LoaderSlot>
    }
  >
    <Typography variant="body2" fontWeight={500} color={theme.colors.text.primary} align="center">
      Please wait while your transaction is being processed.
    </Typography>
  </StatusScreen>
);

export const SendSuccessScreen = ({
  amount,
  symbol,
  digest,
}: {
  amount: string;
  symbol: string;
  digest?: string | null;
}) => (
  <StatusScreen
    glyph={
      <Glyph $tone="success">
        <CheckIcon />
      </Glyph>
    }
  >
    <TextGroup>
      <Typography variant="body1" fontWeight={600} color={theme.colors.text.primary} align="center">
        Transfer Successful!
      </Typography>
      {amount ? (
        <Typography variant="body2" color={theme.colors.text.secondary} align="center">
          <Typography as="span" variant="body2" color={theme.colors.text.primary} fontWeight={500}>
            {amount} {symbol}
          </Typography>{' '}
          has been sent successfully.
        </Typography>
      ) : null}
      {digest && (
        <Link
          href={getSuiExplorerTransactionUrl(digest)}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
        >
          <ExternalLinkIcon />
          View transaction
        </Link>
      )}
    </TextGroup>
  </StatusScreen>
);

export const SendErrorScreen = ({ message }: { message: string | null }) => (
  <StatusScreen
    glyph={
      <Glyph $tone="error">
        <AlertIcon />
      </Glyph>
    }
  >
    <TextGroup>
      <Typography variant="body1" fontWeight={600} color={theme.colors.text.primary} align="center">
        Something went wrong.
      </Typography>
      <Typography variant="body2" color={theme.colors.text.secondary} align="center">
        {message ?? 'Your funds were not sent.'}
      </Typography>
    </TextGroup>
  </StatusScreen>
);
